/**
 * PROTOTYPE spread -> real pricing adapter.
 *
 * Ties a spread to the EXISTING pricing engine by building the same PricingRequest
 * the proposal flow sends and calling proposalService.calculatePrice. NO formula is
 * re-implemented here; we only shape the request and read the breakdown back. One
 * request per spread carries every wired module, and the engine prices them together.
 *
 * Modules wired so far: Welding (hardware machines + duration) and Material
 * Traceability (mills + add-ons + per-spread config-rate overrides). The remaining
 * modules are still to come, so a single spread with default config can be checked
 * cent-for-cent against a normal proposal.
 */
import { proposalService } from '../services/proposalService';
import {
  Spread, ProjectState, ModuleKey, MACHINE_KEYS, moduleVolume, moduleSources, durationMonthsFor,
  moduleCfg, moduleCfgOverridden, projectCfgOverridden,
} from './spreadSplitData';
import { ModuleSource } from './spreadSourceData';
import { MODULE_DEFAULTS } from './spreadConfigDefs';

export type WeldMode = 'live' | 'offline';
export interface SpreadRequestOpts { mode: WeldMode; mobilizing: boolean; }

/** One module's engine result: the customer price, the internal cost (COGS), and the raw breakdown. */
export interface ModulePriceResult { total: number; cost: number; details: Record<string, unknown>; }
export interface SpreadPriceResult {
  welding: ModulePriceResult; mt: ModulePriceResult; ndt: ModulePriceResult; coating: ModulePriceResult;
  platform: ModulePriceResult; retention: ModulePriceResult;
}

/** Project welding services (support / training / provisioning / on-field), priced once.
 *  The engine books services at cost, so total == cost (no margin). */
export interface ServiceComponent { label: string; amount: number; }
export interface ServicesPriceResult { total: number; cost: number; components: ServiceComponent[]; }

// Prototype machine keys already match the backend welding system keys (p625/m500/manual/iwm).
export function buildWeldingProduct(spread: Spread, opts: SpreadRequestOpts): Record<string, unknown> {
  const items: Record<string, unknown> = {};
  MACHINE_KEYS.forEach((k) => {
    const qty = spread.machines[k];
    if (qty > 0) {
      items[k] = { selected: true, quantity: qty, spareQuantity: 0 };
      items[`${k}_${opts.mode}`] = { selected: true }; // required mode sibling (backend raises without it)
    }
  });
  // Mobilization is only computed when the mobilizing service is selected (welding.py).
  if (opts.mobilizing) items.mobilizing = { selected: true };
  return { isSelected: true, items, overrides: {} };
}

// The prototype config drawer stores these as display percents (75, 10, 98); the
// engine wants them as fractions (0.75, 0.10, 0.98). durationDiscountPct stays a
// percent and the difficulty multipliers stay as-is, matching the proposal flow.
const FRACTION_KEYS = new Set(['marginTarget', 'qaOverheadPct', 'autoExtractRate', 'nre.validationOverhead', 'nre.marginTarget']);

/** Per-spread config-rate overrides for a module: every rate the spread or the project
 *  rate card overrides, keyed by the engine's config field, unit-converted. */
export function buildModuleOverrides(project: ProjectState, spread: Spread, module: ModuleKey): Record<string, number> {
  const out: Record<string, number> = {};
  Object.keys(MODULE_DEFAULTS[module] ?? {}).forEach((key) => {
    if (!moduleCfgOverridden(spread, module, key) && !projectCfgOverridden(project, module, key)) return;
    const v = moduleCfg(project, spread, module, key);
    out[key] = FRACTION_KEYS.has(key) ? v / 100 : v;
  });
  return out;
}

/** MT overrides (kept as a named helper for the price hook's refetch key). */
export function buildMtOverrides(project: ProjectState, spread: Spread): Record<string, number> {
  return buildModuleOverrides(project, spread, 'mt');
}

// NDT/Coating name their reports-per-record config key differently (reportsPerInspection /
// reportsPerJoint), but the engine's onboarding NRE reads "reportsPerPipe"; without it the
// NRE build hours are stuck at zero. So pin the effective report count on each row for those
// modules. MT's config already has reportsPerPipe, so it needs nothing extra.
const REPORTS_KEY: Record<'ndt' | 'coating', string> = { ndt: 'reportsPerInspection', coating: 'reportsPerJoint' };

// Each source (mill / vendor / contractor) becomes an engine row; the backend reads the
// record count off pipeCount for every module.
function sourceRows(sources: ModuleSource[], reportsPerRecord?: number): Record<string, unknown>[] {
  return sources.map((s) => ({
    id: s.id, name: s.name, sourceArrivalType: s.format,
    dataTypes: [] as string[], pipeCount: Math.max(0, s.pipeCount), chargeNre: s.chargeNre,
    ...(reportsPerRecord != null ? { reportsPerPipe: reportsPerRecord } : {}),
  }));
}

// Material Traceability: mills are the data sources, volume is the summed record count,
// the three flat add-ons ride in options, and the spread's overridden rate card rides in
// overrides. Same shape the proposal flow sends, so a spread configured like a normal
// proposal prices identically.
export function buildMaterialTraceabilityProduct(project: ProjectState, spread: Spread): Record<string, unknown> {
  const { gas, wire, coatingMaterials } = spread.mtOptions;
  return {
    isSelected: true,
    volume: moduleVolume(spread, 'mt'),
    sources: sourceRows(spread.mtMills),
    options: { gas, wire, coatingMaterials },
    overrides: buildMtOverrides(project, spread),
  };
}

// NDT (vendors, per inspection) and Coating (contractors, per joint) are the same
// volume-driven shape as MT: sources drive volume + onboarding NRE, overrides carry the
// rate card. No module-level options for these.
export function buildModuleProduct(project: ProjectState, spread: Spread, module: 'ndt' | 'coating'): Record<string, unknown> {
  const reportsPerRecord = moduleCfg(project, spread, module, REPORTS_KEY[module]);
  return {
    isSelected: true,
    volume: moduleVolume(spread, module),
    sources: sourceRows(moduleSources(spread, module), reportsPerRecord),
    overrides: buildModuleOverrides(project, spread, module),
  };
}

// Disabled modules are marked isSelected: false so the engine skips them; that also drops
// them from priced_new_modules, so the platform infra scales with the enabled modules only.
const select = (product: Record<string, unknown>, enabled: boolean): Record<string, unknown> => ({ ...product, isSelected: enabled });

export function buildSpreadRequest(project: ProjectState, spread: Spread, months: number, opts: SpreadRequestOpts): Record<string, unknown> {
  const platform = spread.platformOptions;
  const retention = spread.retentionOptions;
  const on = spread.enabled;
  const platformOverrides = { ...buildModuleOverrides(project, spread, 'platform') };
  if (!platform.support) platformOverrides.baseSupportHours = 0; // support off -> drop the support hours
  return {
    selectedProducts: {
      welding: select(buildWeldingProduct(spread, opts), on.welding),
      materialTraceability: select(buildMaterialTraceabilityProduct(project, spread), on.mt),
      ndt: select(buildModuleProduct(project, spread, 'ndt'), on.ndt),
      coating: select(buildModuleProduct(project, spread, 'coating'), on.coating),
    },
    timeline: { startDate: '', endDate: '', duration: { value: String(months), unit: 'months' }, milestones: [] },
    // Each spread is its own mini-project, so it carries its own platform + retention.
    // Platform and retention are independent: either can be included without
    // the other, so neither toggle silences the other's line.
    includePlatform: on.platform,
    numberOfUsers: platform.users,
    customerManagedCloud: !platform.infra, // infra off -> customer-managed cloud, infra fee zeroed
    retentionTier: on.retention && retention.tier !== 'none' ? retention.tier : null,
    retentionYears: retention.years,
    platformOverrides,
    retentionOverrides: buildModuleOverrides(project, spread, 'retention'),
  };
}

// The engine's duration months must be a whole number (it int()s the value), and the
// proposal UI only ever sends integers. A spread's perDay-derived duration can be
// fractional (e.g. 4.7), so round to whole months. MT shares the same project duration.
export function weldingMonths(spread: Spread): number {
  return Math.max(1, Math.round(durationMonthsFor(spread)));
}

function moduleResult(product: Record<string, unknown> | undefined): ModulePriceResult {
  const details = (product?.details ?? {}) as Record<string, unknown>;
  return { total: Number((product?.total as number) ?? 0), cost: Number((details.cost as number) ?? 0), details };
}

// breakdown.projectLevel is the project-lines dict: platform PRICE is
// recurring.platformContractTotal, retention PRICE is recurring.retentionTotal, and the
// only modelled cost is the platform base fee's baseCost (monthly x months). Per-user and
// retention are value-set (no COGS), so they read as pure margin.
function projectLevelResults(projectLevel: Record<string, unknown> | null | undefined): { platform: ModulePriceResult; retention: ModulePriceResult } {
  const recurring = (projectLevel?.recurring ?? {}) as Record<string, unknown>;
  const months = Number(recurring.months ?? 0);
  const components = (recurring.components ?? []) as Array<Record<string, unknown>>;
  const base = components.find((c) => c.label === 'Platform base fee');
  const details = (projectLevel ?? {}) as Record<string, unknown>;
  return {
    platform: { total: Number(recurring.platformContractTotal ?? 0), cost: Number((base?.baseCost as number) ?? 0) * months, details },
    retention: { total: Number(recurring.retentionTotal ?? 0), cost: 0, details },
  };
}

export async function fetchSpreadPrice(project: ProjectState, spread: Spread, opts: SpreadRequestOpts): Promise<SpreadPriceResult> {
  const months = weldingMonths(spread);
  const body = buildSpreadRequest(project, spread, months, opts);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res: any = await proposalService.calculatePrice(body as any);
  // A disabled module is absent from the response; moduleResult reads that as $0.
  const products = (res?.breakdown?.products ?? {}) as Record<string, Record<string, unknown>>;
  const { platform, retention } = projectLevelResults(res?.breakdown?.projectLevel);
  return {
    welding: moduleResult(products.welding),
    mt: moduleResult(products.materialTraceability),
    ndt: moduleResult(products.ndt),
    coating: moduleResult(products.coating),
    platform,
    retention,
  };
}

// The engine's welding services live in the otherCosts breakdown, keyed like the request.
const SERVICE_READBACK: { key: string; label: string }[] = [
  { key: 'remoteSupport', label: 'Support' },
  { key: 'training', label: 'Training' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'onFieldSupport', label: 'On-field support' },
];

// Project welding services are billed once, so they get their own engine call. The engine
// rejects a machine-less welding request, so we add one phantom offline machine purely to
// satisfy that guard (it does not enter any service formula and we read only the service
// fields). Duration 1 makes "support hours" a flat total, matching the settings field.
export function buildServicesRequest(project: ProjectState): Record<string, unknown> {
  const sv = project.services;
  const items: Record<string, unknown> = {
    manual: { selected: true, quantity: 1, spareQuantity: 0 },
    manual_offline: { selected: true },
  };
  if (sv.supportHours > 0) items.remoteSupport = { selected: true, quantity: sv.supportHours };
  if (sv.trainingTier !== 'none') items.training = { selected: true, quantity: 1, trainingDurationType: sv.trainingTier };
  if (sv.provisioningHours > 0) items.provisioning = { selected: true, quantity: sv.provisioningHours };
  if (sv.onFieldDays > 0) items.onFieldSupport = { selected: true, quantity: sv.onFieldDays };
  return {
    selectedProducts: { welding: { isSelected: true, items, overrides: {} } },
    timeline: { startDate: '', endDate: '', duration: { value: '1', unit: 'months' }, milestones: [] },
    includePlatform: false,
    numberOfUsers: 0,
    customerManagedCloud: true,
    retentionTier: null,
    retentionYears: 0,
    platformOverrides: {},
    retentionOverrides: {},
  };
}

export async function fetchServicesPrice(project: ProjectState): Promise<ServicesPriceResult> {
  const body = buildServicesRequest(project);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res: any = await proposalService.calculatePrice(body as any);
  const otherCosts = (res?.breakdown?.products?.welding?.details?.otherCosts ?? {}) as Record<string, number>;
  const components = SERVICE_READBACK
    .map(({ key, label }) => ({ label, amount: Number(otherCosts[key] ?? 0) }))
    .filter((c) => c.amount > 0);
  const total = components.reduce((sum, c) => sum + c.amount, 0);
  return { total, cost: total, components }; // services are cost pass-through: price == cost
}
